import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../i18n';
import { api, ApiError, qs } from '../lib/api';
import { useCustomer } from '../lib/queries';
import { isEmail, isValidIsraeliId } from '../lib/validation';
import type { CustomerStatus, Duplicate, PaymentTerms } from '../lib/types';
import { Button, Callout, Checkbox, Dialog, ErrorState, Field, Input, Select, Textarea, useDebounced, useToast, useUnsavedWarning } from '../components/ui';
import { AddressFields, addressToPayload, emptyAddress, type AddressDraft } from '../components/AddressFields';
import { CustomerPicker } from '../components/CustomerPicker';
import { IconPlus, IconTrash } from '../components/icons';

type Form = {
  businessName: string;
  companyNumber: string;
  noCompanyNumber: boolean;
  taxId: string;
  contactName: string;
  phone: string;
  email: string;
  website: string;
  status: CustomerStatus;
  paymentTerms: PaymentTerms;
  paymentDay: string;
  parentId: number | null;
  notes: string;
};

const blank: Form = {
  businessName: '',
  companyNumber: '',
  noCompanyNumber: false,
  taxId: '',
  contactName: '',
  phone: '',
  email: '',
  website: '',
  status: 'active',
  paymentTerms: 'EOM_30',
  paymentDay: '',
  parentId: null,
  notes: '',
};

export function CustomerFormPage() {
  const { id } = useParams();
  const editId = id ? Number(id) : 0;
  const isEdit = editId > 0;
  const { data: existing, error: loadError, isLoading } = useCustomer(editId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [f, setF] = useState<Form>(blank);
  const [addresses, setAddresses] = useState<AddressDraft[]>([emptyAddress(true)]);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [dupDialog, setDupDialog] = useState<Duplicate[] | null>(null);
  const [initial, setInitial] = useState<string>(JSON.stringify(blank));
  const [saved, setSaved] = useState(false);
  const dirty = !saved && JSON.stringify(f) !== initial;
  useUnsavedWarning(dirty);

  useEffect(() => {
    if (!existing) return;
    const loaded: Form = {
      businessName: existing.businessName,
      companyNumber: existing.companyNumber ?? '',
      noCompanyNumber: !existing.companyNumber,
      taxId: existing.taxId ?? '',
      contactName: existing.contactName ?? '',
      phone: existing.phone ?? '',
      email: existing.email ?? '',
      website: existing.website ?? '',
      status: existing.status,
      paymentTerms: existing.paymentTerms,
      paymentDay: existing.paymentDay?.toString() ?? '',
      parentId: existing.parentId,
      notes: existing.notes ?? '',
    };
    setF(loaded);
    setInitial(JSON.stringify(loaded));
  }, [existing]);

  // Live duplicate hint while typing.
  const dName = useDebounced(f.businessName.trim(), 400);
  const dCN = useDebounced(f.companyNumber.replace(/\D/g, ''), 400);
  const { data: dupHint } = useQuery({
    queryKey: ['dup-check', dName, dCN, editId],
    queryFn: () => api<{ duplicates: Duplicate[] }>(`/api/customers/check-duplicates${qs({ businessName: dName, companyNumber: dCN, excludeId: editId || undefined })}`),
    enabled: dName.length >= 2 || dCN.length >= 5,
  });

  const errors = useMemo(() => {
    const e: Partial<Record<keyof Form | 'addresses', string>> = {};
    if (f.businessName.trim().length < 2) e.businessName = t.common.required;
    const cn = f.companyNumber.replace(/\D/g, '');
    if (!f.noCompanyNumber && !cn) e.companyNumber = 'יש להזין ח.פ / ע.מ, או לסמן שאין ללקוח מספר';
    else if (cn && (cn.length < 5 || cn.length > 9)) e.companyNumber = 'ח.פ / ע.מ חייב להכיל 5–9 ספרות';
    if (f.email.trim() && !isEmail(f.email)) e.email = 'כתובת אימייל לא תקינה';
    if (f.paymentTerms === 'EOM_FIXED_DAY' && !(Number(f.paymentDay) >= 1 && Number(f.paymentDay) <= 31)) e.paymentDay = 'יש להזין יום בין 1 ל־31';
    if (!isEdit && addresses.some((a) => !a.label.trim() && (a.street || a.city))) e.addresses = 'לכל כתובת יש לתת שם (למשל "משרד ראשי")';
    return e;
  }, [f, addresses, isEdit]);
  const cnDigits = f.companyNumber.replace(/\D/g, '');
  const cnChecksumWarn = cnDigits.length >= 5 && cnDigits.length <= 9 && !isValidIsraeliId(cnDigits);

  const set = (k: keyof Form) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const submit = async (e?: FormEvent, confirmDuplicate = false) => {
    e?.preventDefault();
    setTouched(true);
    setServerError(null);
    if (Object.keys(errors).length) return;
    setBusy(true);
    const customer = {
      businessName: f.businessName.trim(),
      companyNumber: f.noCompanyNumber ? null : cnDigits || null,
      taxId: f.taxId.trim() || null,
      contactName: f.contactName.trim() || null,
      phone: f.phone.trim() || null,
      email: f.email.trim() || null,
      website: f.website.trim() || null,
      status: f.status,
      paymentTerms: f.paymentTerms,
      paymentDay: f.paymentTerms === 'EOM_FIXED_DAY' ? Number(f.paymentDay) : null,
      parentId: f.parentId,
      notes: f.notes.trim() || null,
    };
    try {
      let targetId = editId;
      if (isEdit) {
        await api(`/api/customers/${editId}`, { method: 'PATCH', body: { customer, confirmDuplicate } });
        toast.success(t.common.savedToast);
      } else {
        const valid = addresses.filter((a) => a.label.trim() || a.street.trim() || a.city.trim());
        const r = await api<{ id: number }>('/api/customers', { body: { customer, addresses: valid.map(addressToPayload), confirmDuplicate } });
        targetId = r.id;
        toast.success(t.customerForm.created);
      }
      for (const k of ['customers', 'customer', 'customer-options', 'dashboard']) qc.invalidateQueries({ queryKey: [k] });
      setSaved(true);
      navigate(`/customers/${targetId}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && (err.details as { duplicates?: Duplicate[] })?.duplicates) {
        setDupDialog((err.details as { duplicates: Duplicate[] }).duplicates);
      } else {
        setServerError(err instanceof ApiError ? err.message : t.common.unknownError);
      }
    } finally {
      setBusy(false);
    }
  };

  if (isEdit && loadError) return <div className="panel"><ErrorState error={loadError} /></div>;
  if (isEdit && isLoading) return <div className="panel panel-body"><span className="skeleton" style={{ width: '40%' }} /></div>;

  const err = (k: keyof typeof errors) => (touched ? (errors[k] ?? null) : null);

  return (
    <form onSubmit={submit} noValidate>
      <div className="page-head">
        <div>
          <div className="crumbs">
            <Link to="/customers">{t.customers.title}</Link>
            {isEdit && existing ? <> / <Link to={`/customers/${editId}`}>{existing.businessName}</Link></> : null}
          </div>
          <h1>{isEdit ? t.customerForm.titleEdit : t.customerForm.titleNew}</h1>
        </div>
        <div className="row">
          <Button onClick={() => navigate(isEdit ? `/customers/${editId}` : '/customers')}>{t.common.cancel}</Button>
          <Button type="submit" variant="primary" loading={busy}>
            {isEdit ? t.common.save : t.customerForm.create}
          </Button>
        </div>
      </div>

      <div className="grid-main-side">
        <div className="panel panel-body">
          {serverError ? <Callout tone="bad">{serverError}</Callout> : null}

          <div className="form-section">
            <div className="form-section-title">{t.customerForm.business}</div>
            <div className="form-grid">
              <Field label={t.customerForm.businessName} required hint={t.customerForm.businessNameHint} error={err('businessName')} className="span-2">
                <Input value={f.businessName} onChange={set('businessName')} aria-invalid={Boolean(err('businessName'))} autoFocus={!isEdit} />
              </Field>
              <Field
                label={t.customerForm.companyNumber}
                required={!f.noCompanyNumber}
                error={err('companyNumber')}
                hint={cnChecksumWarn ? <span style={{ color: 'var(--warn)' }}>{t.customerForm.checksumWarn}</span> : t.customerForm.companyNumberHint}
              >
                <Input
                  value={f.companyNumber}
                  onChange={set('companyNumber')}
                  disabled={f.noCompanyNumber}
                  ltr
                  inputMode="numeric"
                  aria-invalid={Boolean(err('companyNumber'))}
                />
                <Checkbox checked={f.noCompanyNumber} onChange={(v) => setF({ ...f, noCompanyNumber: v, companyNumber: v ? '' : f.companyNumber })}>
                  <span className="small">{t.customerForm.noCompanyNumber}</span>
                </Checkbox>
              </Field>
              <Field label={t.customerForm.status}>
                <Select value={f.status} onChange={set('status')}>
                  {(Object.keys(t.enums.customerStatus) as CustomerStatus[]).map((s) => (
                    <option key={s} value={s}>{t.enums.customerStatus[s]}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t.customerForm.contact}>
                <Input value={f.contactName} onChange={set('contactName')} />
              </Field>
              <Field label={t.customerForm.phone}>
                <Input value={f.phone} onChange={set('phone')} ltr inputMode="tel" />
              </Field>
              <Field label={t.customerForm.email} error={err('email')}>
                <Input value={f.email} onChange={set('email')} ltr type="email" aria-invalid={Boolean(err('email'))} />
              </Field>
              <Field label={t.customerForm.website}>
                <Input value={f.website} onChange={set('website')} ltr />
              </Field>
              <Field label={t.customerForm.taxId}>
                <Input value={f.taxId} onChange={set('taxId')} ltr />
              </Field>
              <Field label={t.customerForm.parent} hint="לדוגמה: מחלקה בעירייה או סניף של רשת">
                <div className="row">
                  <div style={{ flex: 1 }}>
                    <CustomerPicker value={f.parentId} onChange={(v) => setF({ ...f, parentId: v })} exclude={editId ? [editId] : []} onlyTopLevel placeholder={t.customerForm.noParent} />
                  </div>
                  {f.parentId ? (
                    <Button size="sm" variant="ghost" onClick={() => setF({ ...f, parentId: null })}>
                      הסרה
                    </Button>
                  ) : null}
                </div>
              </Field>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-title">{t.customerForm.commercial}</div>
            <div className="form-grid">
              <Field label={t.customerForm.terms}>
                <Select value={f.paymentTerms} onChange={set('paymentTerms')}>
                  {(Object.keys(t.enums.paymentTerms) as PaymentTerms[]).map((s) => (
                    <option key={s} value={s}>{t.enums.paymentTerms[s]}</option>
                  ))}
                </Select>
              </Field>
              {f.paymentTerms === 'EOM_FIXED_DAY' ? (
                <Field label={t.customerForm.paymentDay} required hint={t.customerForm.paymentDayHint} error={err('paymentDay')}>
                  <Input type="number" min={1} max={31} value={f.paymentDay} onChange={set('paymentDay')} ltr />
                </Field>
              ) : null}
            </div>
          </div>

          {!isEdit ? (
            <div className="form-section">
              <div className="row" style={{ marginBottom: 12 }}>
                <div className="form-section-title" style={{ margin: 0 }}>{t.customerForm.addresses}</div>
                <span className="spacer" />
                <Button size="sm" onClick={() => setAddresses([...addresses, emptyAddress(addresses.length === 0)])}>
                  <IconPlus /> {t.customerForm.addAddress}
                </Button>
              </div>
              {touched && errors.addresses ? <Callout tone="bad">{errors.addresses}</Callout> : null}
              <div className="stack">
                {addresses.map((a, i) => (
                  <div key={i} className="panel panel-body" style={{ background: 'var(--surface-2)' }}>
                    <div className="row" style={{ marginBottom: 8 }}>
                      <span className="small muted">כתובת {i + 1}</span>
                      <span className="spacer" />
                      <Button size="sm" variant="ghost" icon aria-label={t.common.delete} onClick={() => setAddresses(addresses.filter((_, j) => j !== i))}>
                        <IconTrash />
                      </Button>
                    </div>
                    <AddressFields
                      value={a}
                      onChange={(next) =>
                        setAddresses(addresses.map((x, j) => (j === i ? next : next.isPrimary ? { ...x, isPrimary: false } : x)))
                      }
                    />
                  </div>
                ))}
                {addresses.length === 0 ? <div className="muted small">{t.address.empty}</div> : null}
              </div>
            </div>
          ) : (
            <div className="form-section muted small">כתובות מנוהלות בלשונית "פרטי העסק" בכרטיס הלקוח.</div>
          )}

          <div className="form-section">
            <Field label={t.customerForm.notes}>
              <Textarea value={f.notes} onChange={set('notes')} />
            </Field>
          </div>
        </div>

        <aside className="stack">
          {dupHint && dupHint.duplicates.length ? (
            <Callout tone="warn" title={t.customerForm.duplicateTitle}>
              <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
                {dupHint.duplicates.map((d) => (
                  <li key={d.id}>
                    <Link to={`/customers/${d.id}`}>{d.businessName}</Link> <span className="muted small">— {d.reason}</span>
                  </li>
                ))}
              </ul>
            </Callout>
          ) : null}
          <div className="panel panel-body small muted">
            <div className="strong" style={{ color: 'var(--ink)', marginBottom: 4 }}>איך זה עובד</div>
            אחרי שמירת הלקוח, כל חשבונית, קבלה, תעודת משלוח או הצעת מחיר שתפיקו לו במורנינג תשויך לכרטיס אוטומטית — לפי הח.פ, ואם אין — לפי השם שמופיע במסמך.
          </div>
        </aside>
      </div>

      <Dialog
        open={Boolean(dupDialog)}
        onClose={() => setDupDialog(null)}
        title={t.customerForm.duplicateTitle}
        footer={
          <>
            <Button
              variant="primary"
              onClick={() => {
                setDupDialog(null);
                void submit(undefined, true);
              }}
            >
              {t.customerForm.createAnyway}
            </Button>
            <Button onClick={() => setDupDialog(null)}>{t.common.cancel}</Button>
          </>
        }
      >
        <p style={{ marginBottom: 10 }}>{t.customerForm.duplicateBody}</p>
        <div className="panel">
          <table className="table">
            <tbody>
              {dupDialog?.map((d) => (
                <tr key={d.id}>
                  <td className="primary">{d.businessName}</td>
                  <td className="muted">{d.reason}</td>
                  <td className="num">
                    <Link to={`/customers/${d.id}`} className="btn sm">{t.customerForm.openExisting}</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Dialog>
    </form>
  );
}
